// @flow
import { sprintf } from 'sprintf-js';
import * as React from 'react';
import queryString from 'query-string';
import { withRouter } from 'react-router';
import type { Location, RouterHistory } from 'react-router';
import styled from 'styled-components';
import { matchSize } from 'utils/responsive';
import Button from 'components/Button';
import Box from 'components/Box';
import Icon from 'components/Icon';
import { BodySmall } from 'components/Text';
import Hide from 'components/Hide';
import { colors, fontWeights, gutterWidth, transition } from '../theme';

const PageLink = styled(Button)`
  padding: 4px;
  margin: 0 10px;
  color: ${colors.greyishBrown};
  font-weight: ${fontWeights.semiBold};
  &,
  &:active,
  &:visited,
  &:hover {
    text-decoration: none;
  }
  &:hover,
  &:focus {
    color: ${colors.niceBlue};
  }
`;

const CurrentPage = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: default;
  background-color: ${colors.waterBlue};
  border-radius: 50%;
  text-align: center;
  width: 40px;
  height: 40px;
  color: white;
  font-weight: ${fontWeights.semiBold};
`;

const ArrowLink = styled(Button).attrs(({ $disabled }) => ({
  tabIndex: $disabled ? -1 : 0
}))`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  &,
  svg {
    transition: all ${transition};
  }
  svg {
    * {
      fill: ${colors.waterBlue};
    }
  }
  ${props =>
    props.$disabled &&
    `
    pointer-events: none;
    svg {
      * {
        fill: ${colors.silver};
      }
    }
  `}
  &:hover, &:focus {
    background: ${colors.offWhite};
    svg {
      * {
        fill: ${colors.niceBlue};
      }
    }
  }
`;

const Page = ({
  pageNumber,
  selected,
  onClick
}: {
  pageNumber: number,
  selected: boolean,
  onClick?: (SyntheticEvent<any>) => void
}) =>
  selected ? (
    <CurrentPage data-qa-id="pagination-current-page">
      <BodySmall>{pageNumber + 1}</BodySmall>
    </CurrentPage>
  ) : (
    <PageLink data-qa-id="pagination-link" onClick={onClick}>
      <BodySmall>{pageNumber + 1}</BodySmall>
    </PageLink>
  );

// Number of pages before and after the current page we want to show links to
const PAGINATION_RADIUS = matchSize({ xs: true }) ? 1 : 2;

const getResultsDescription = ({
  pageIndex,
  hitsPerPage,
  nbHits
}: {
  pageIndex: number,
  hitsPerPage: number,
  nbHits: number
}) =>
  /* TRANSLATORS: pagination description */ sprintf(
    getText('%(first)s-%(last)s of %(total)s'),
    {
      first: (pageIndex * hitsPerPage + 1).toLocaleString(),
      last: Math.min((pageIndex + 1) * hitsPerPage, nbHits).toLocaleString(),
      total: nbHits.toLocaleString()
    }
  );

const Pagination = ({
  nbHits,
  nbPages,
  hitsPerPage,
  pageIndex,
  setPageIndex,
  history,
  location,
  onClick,
  pageParam = 'page'
}: {
  nbHits: number,
  nbPages: number,
  history: RouterHistory,
  hitsPerPage: number,
  pageIndex: number,
  setPageIndex?: number => null,
  location: Location,
  onClick?: (SyntheticEvent<any>) => void,
  pageParam?: string
}) => {
  if (nbPages < 2) return null;

  const pageLink = (index: number) => {
    const params = queryString.parse(location.search);
    if (index > 0) {
      params[pageParam] = index + 1;
    } else {
      delete params[pageParam];
    }
    return `?${queryString.stringify(params)}`;
  };

  const goToPage = (e, index) => {
    if (setPageIndex) {
      setPageIndex(index);
    } else {
      history.push(pageLink(index));
      if (onClick) {
        onClick(e);
      }
    }
  };

  // Total number of page links to show
  const pageLinkCount = PAGINATION_RADIUS * 2 + 1;
  const allPages = [];
  for (let i = 0; i < nbPages; i++) {
    allPages.push(i);
  }
  const startIndex = Math.max(pageIndex - PAGINATION_RADIUS, 0);
  const endIndex = startIndex + pageLinkCount;

  const pageLinksToShow =
    endIndex > nbPages
      ? // We are at the end of the list
        allPages.slice(-pageLinkCount)
      : // We are in the middle or beginning of list
        allPages.slice(startIndex, endIndex);

  return (
    <Box
      mt={[gutterWidth / 2, null, null, 60]}
      pt={[gutterWidth / 2, null, null, 60]}
      borderTop={`1px solid ${colors.aluminum}`}
    >
      <BodySmall>
        <Box
          display="flex"
          flexDirection={['column', null, 'row']}
          alignItems="center"
          justifyContent={['center', null, 'space-between']}
        >
          {getResultsDescription({ pageIndex, hitsPerPage, nbHits })}
          <Box
            as="nav"
            display="flex"
            alignItems="center"
            justifyContent={['center', null, 'flex-end']}
            flex="1 1 auto"
            data-qa-id="pagination"
          >
            <ArrowLink
              data-qa-id="pagination-link-previous"
              $disabled={pageIndex === 0}
              aria-label={getText('Previous page')}
              onClick={e => {
                goToPage(e, pageIndex - 1);
              }}
            >
              <Icon name="chevron" width={16} height={9} direction="left" />
            </ArrowLink>
            {pageLinksToShow[0] > 0 && (
              <>
                <Page
                  key={0}
                  pageNumber={0}
                  to={pageLink(0)}
                  selected={false}
                  onClick={e => {
                    goToPage(e, 0);
                  }}
                  disabled={pageIndex === 0}
                />
                {pageLinksToShow[0] > 1 && (
                  <Box pr={['8px', 0]} color={colors.silver}>
                    ...
                  </Box>
                )}
              </>
            )}
            {pageLinksToShow.map(pageNumber => (
              <Hide
                key={pageNumber}
                // only show 3 pages on small mobile screens
                // otherwise paging will wrap on iPhone SE
                xs={
                  pageNumber !== pageIndex &&
                  pageNumber !== nbPages - 1 &&
                  (pageNumber === pageLinksToShow[0] ||
                    pageNumber === pageLinksToShow[pageLinksToShow.length - 1])
                }
              >
                <Page
                  key={pageNumber}
                  pageNumber={pageNumber}
                  selected={pageNumber === pageIndex}
                  onClick={e => {
                    goToPage(e, pageNumber);
                  }}
                />
              </Hide>
            ))}
            {pageLinksToShow[pageLinksToShow.length - 1] < nbPages - 1 && (
              <>
                {pageLinksToShow[pageLinksToShow.length - 1] < nbPages - 2 && (
                  <Box pl={['8px', 0]} color={colors.silver}>
                    ...
                  </Box>
                )}
                <Page
                  key={nbPages - 1}
                  pageNumber={nbPages - 1}
                  selected={false}
                  onClick={e => {
                    goToPage(e, nbPages - 1);
                  }}
                />
              </>
            )}
            <ArrowLink
              data-qa-id="pagination-link-next"
              $disabled={pageIndex === nbPages - 1}
              onClick={e => {
                goToPage(e, pageIndex + 1);
              }}
              aria-label={getText('Next page')}
            >
              <Icon name="chevron" width={16} height={9} direction="right" />
            </ArrowLink>
          </Box>
        </Box>
      </BodySmall>
    </Box>
  );
};

export default withRouter(Pagination);
